import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAdminSections } from "@/hooks/useAdminSections";
import { LogOut, FileText, Shield, KeyRound } from "lucide-react";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LoginModal = ({ isOpen, onClose }: LoginModalProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: "Error de inicio de sesión",
          description: "Credenciales incorrectas. Por favor intenta de nuevo.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "¡Bienvenido!",
          description: "Has iniciado sesión correctamente.",
        });
        setEmail("");
        setPassword("");
        onClose();
        navigate('/empleados');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Ocurrió un error al iniciar sesión.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            Acceso Empleados REA Managers
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Ingresa tus credenciales para acceder al panel de empleados
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleLogin} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              type="email"
              placeholder="tu@reamanagers.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal = ({ isOpen, onClose }: ChangePasswordModalProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({ title: "Error", description: "La contraseña debe tener al menos 6 caracteres.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "Las contraseñas no coinciden.", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "¡Listo!", description: "Tu contraseña ha sido actualizada correctamente." });
        setNewPassword("");
        setConfirmPassword("");
        onClose();
      }
    } catch {
      toast({ title: "Error", description: "Ocurrió un error al cambiar la contraseña.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">Cambiar contraseña</DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">Ingresa tu nueva contraseña</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">Nueva contraseña</Label>
            <Input id="new-password" type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirmar contraseña</Label>
            <Input id="confirm-password" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} />
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Actualizando..." : "Cambiar contraseña"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface UserMenuProps {
  userEmail: string;
  onLogout: () => void;
  showDashboardLink?: boolean;
}

export const UserMenu = ({ userEmail, onLogout, showDashboardLink = false }: UserMenuProps) => {
  const { hasAnyPermission, loading } = useAdminSections();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  return (
    <>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden md:inline">
          {userEmail}
        </span>
        {showDashboardLink && (
          <Link to="/empleados">
            <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
              <FileText className="h-4 w-4 mr-1" />
              Panel
            </Button>
          </Link>
        )}
        {!loading && hasAnyPermission && (
          <Link to="/admin">
            <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
              <Shield className="h-4 w-4 mr-1" />
              Admin
            </Button>
          </Link>
        )}
        <Button variant="ghost" size="sm" onClick={() => setIsPasswordModalOpen(true)} className="text-foreground hover:text-primary">
          <KeyRound className="h-4 w-4 mr-1" />
          <span className="hidden md:inline">Contraseña</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={onLogout} className="text-foreground hover:text-primary">
          <LogOut className="h-4 w-4 mr-1" />
          Salir
        </Button>
      </div>
      <ChangePasswordModal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} />
    </>
  );
};
